import { type ActionEvent, Controller } from "@hotwired/stimulus";
import { appendDie, createPool, parse, type Roll, stepNumber, trailingNumber } from "../dice/pool";
import { barSubject } from "../dice/bar_subject";
import { onPendingBonus, setPendingBonus } from "../dice/pending";
import { type DiceStates, diceStates, diceVolume, heldDiceStates, holdDiceStates, saveDiceStates, setThrow, throwEnabled } from "../dice/preferences";
import { type FailureReason, rollAndPost, type Subject } from "../dice/roll_and_post";
import { sheetRollUrl } from "../dice/sheet_roll_url";
import { previewDiceSound } from "../dice/sound";

export type { FailureReason };

// The sound button's `data-level`, by `diceVolume`; the component draws each one.
const SOUND_LEVELS = ["off", "low", "mid", "high"];

// Connects to data-controller="dice-roller" (DiceRollerComponent); see docs/architecture/frontend.md.
export default class extends Controller<HTMLElement> {
	static targets = [
		"counter",
		"pool",
		"pills",
		"chip",
		"chipLabel",
		"hint",
		"customText",
		"modifier",
		"formula",
		"secret",
		"sound",
		"throw",
		"rollButton",
		"column",
		"opener",
		"closer",
		"formulaToggle",
		"formulaField",
		"pendingHint",
		"tabModifier",
		"tabSecret",
		"tabThrow",
	];
	static values = { url: String, handle: String, subject: Object, join: Boolean, secretOff: Boolean };
	declare readonly counterTargets: HTMLElement[];
	declare readonly poolTarget: HTMLElement;
	declare readonly pillsTarget: HTMLElement;
	declare readonly chipTargets: HTMLElement[];
	declare readonly chipLabelTargets: HTMLElement[];
	declare readonly hintTarget: HTMLElement;
	declare readonly customTextTarget: HTMLElement;
	declare readonly modifierTarget: HTMLElement;
	declare readonly formulaTarget: HTMLInputElement;
	declare readonly secretTarget: HTMLInputElement;
	declare readonly soundTarget: HTMLElement;
	declare readonly throwTarget: HTMLInputElement;
	declare readonly rollButtonTarget: HTMLButtonElement;
	declare readonly columnTarget: HTMLElement;
	declare readonly openerTarget: HTMLElement;
	declare readonly closerTarget: HTMLElement;
	declare readonly formulaToggleTarget: HTMLInputElement;
	declare readonly formulaFieldTarget: HTMLElement;
	declare readonly hasPendingHintTarget: boolean;
	declare readonly pendingHintTarget: HTMLElement;
	declare readonly tabModifierTarget: HTMLElement;
	declare readonly tabSecretTarget: HTMLElement;
	declare readonly tabThrowTarget: HTMLElement;
	declare readonly urlValue: string;
	declare readonly handleValue: string;
	declare readonly subjectValue: Subject;
	declare readonly hasSubjectValue: boolean;
	declare readonly joinValue: boolean;
	declare readonly secretOffValue: boolean;
	pool = createPool();
	// A typed formula the pool cannot hold, edited as text instead.
	custom = false;
	// Counts edits, so a roll that lands late clears only what it rolled.
	revision = 0;
	busy = false;
	stopPending: (() => void) | undefined;
	// A board's roller, holding a sheet's pending bonus while that sheet's panel is open.
	joined = false;
	// The modifier the last roll carried, which is spent and so not shared with the sheet's chips until the modifier is
	// edited. A roll that may have landed (posted or lost) keeps it spent; a refused one gives it back.
	spent: number | null = null;
	// Set while this controller writes the shared bonus, which it must not follow back.
	publishing = false;

	// Once per element: the board root is permanent, so Turbo reconnects it with its input kept.
	initialize() {
		this.loadStates();
	}

	connect() {
		// A page Turbo restores from its cache brings the formula back, not the pool behind it.
		const restored = this.formulaTarget.value.trim();
		if (restored && !this.custom && !this.pool.notation()) {
			if (/^[+-]\d+$/.test(restored)) this.pool.setModifier(Number(restored));
			else this.custom = !this.pool.load(restored);
		}
		if (this.hasSubjectValue) this.followPending();
		else if (this.joinValue) this.joinPending();
		this.render();
	}

	disconnect() {
		this.stopPending?.();
		if (this.hasSubjectValue || this.joined) setPendingBonus(0);
		this.joined = false;
	}

	followPending() {
		this.stopPending = onPendingBonus((value) => {
			if (this.publishing || value === this.pending) return;
			this.pool.setModifier(value);
			this.compose();
		});
	}

	// The board's roller has no sheet of its own, so its modifier is not a sheet's pending bonus until the panel opens
	// one; its own rolls keep spending it as they do.
	joinPending() {
		if (this.hasSubjectValue || this.joined) return;
		this.joined = true;
		this.followPending();
		this.render();
	}

	leavePending() {
		if (!this.joined) return;
		this.joined = false;
		this.stopPending?.();
		setPendingBonus(0);
	}

	// A modifier held as text has no pool to give to a roll.
	get pending() {
		return this.custom ? 0 : this.pool.modifier;
	}

	// What the sheet may spend: not the modifier a roll already carries, unless it was edited since.
	get shared() {
		return this.spent === this.pending ? 0 : this.pending;
	}

	publish(value: number) {
		this.publishing = true;
		try {
			setPendingBonus(value);
		} finally {
			this.publishing = false;
		}
	}

	add({ params: { die } }: ActionEvent) {
		if (this.custom) this.edit(appendDie(this.formulaTarget.value, Number(die)));
		else {
			this.pool.add(Number(die));
			this.compose();
		}
	}

	// A formula held as text has no pool to take a die from.
	remove({ params: { die } }: ActionEvent) {
		if (this.custom) return;
		this.pool.remove(Number(die));
		this.compose();
	}

	step({ params: { delta } }: ActionEvent) {
		if (this.custom) this.edit(stepNumber(this.formulaTarget.value, Number(delta)));
		else {
			this.pool.step(Number(delta));
			this.compose();
		}
	}

	clear() {
		this.pool.clear();
		this.compose();
	}

	// Typed text refills the pool, or empties it when the pool cannot hold the formula.
	type() {
		this.custom = !this.pool.load(this.formulaTarget.value);
		this.revision += 1;
		this.render();
	}

	edit(text: string) {
		this.formulaTarget.value = text;
		this.type();
	}

	get opened() {
		return !this.columnTarget.hidden;
	}

	open() {
		const openerFocused = document.activeElement === this.openerTarget;
		this.columnTarget.hidden = false;
		this.openerTarget.setAttribute("aria-expanded", "true");
		// The opener is about to hide; a focus left on it would fall back to the body.
		if (openerFocused) this.closerTarget.focus();
	}

	close() {
		const focusInColumn = this.columnTarget.contains(document.activeElement);
		this.columnTarget.hidden = true;
		this.openerTarget.setAttribute("aria-expanded", "false");
		if (focusInColumn) this.openerTarget.focus();
	}

	toggle() {
		if (this.opened) this.close();
		else this.open();
	}

	focus() {
		(this.opened ? this.closerTarget : this.openerTarget).focus();
	}

	remember() {
		this.keep({ hide: this.secretTarget.checked, formula: this.formulaToggleTarget.checked });
	}

	// Saves only what changed here, so a step taken in another tab is not written back over.
	keep(states: DiceStates) {
		holdDiceStates({ ...heldDiceStates(), ...states });
		saveDiceStates(states);
		this.render();
	}

	// Each press takes the sound one step on: medium, high, off, low.
	stepSound() {
		const volume = (diceVolume() + 1) % SOUND_LEVELS.length;
		this.keep(volume ? { mute: false, volume } : { mute: true });
		previewDiceSound();
	}

	rememberThrow() {
		setThrow(this.throwTarget.checked);
		this.render();
	}

	async roll() {
		if (this.busy) return;

		const name = `@${this.handleValue}`;
		const revision = this.revision;
		const formula = this.formulaTarget.value;
		// The sheet's bar spends its formula, modifier included, as the roll starts: a chip must not spend it too.
		const spent = this.hasSubjectValue;
		if (spent && parse(formula).ok) this.clear();
		const outcome = await rollAndPost({
			formula,
			url: (spent ? sheetRollUrl() : this.urlValue) || null,
			subject: this.hasSubjectValue ? this.subjectValue : (barSubject() ?? undefined),
			secret: this.secretTarget.checked,
			onRolling: (roll) => {
				this.dispatch("rolling", { detail: { roll, name } });
				this.spent = this.pending || null;
				this.busy = true;
				this.render();
			},
		});
		if (!outcome) return;

		if (outcome.kind === "secret") {
			this.dispatch("secret", { detail: { roll: outcome.roll, name } });
			if (!spent) this.clear();
			return;
		}
		if (outcome.kind === "posted" || outcome.kind === "local") {
			this.dispatch(outcome.kind === "posted" ? "rolled" : "local", { detail: { roll: outcome.roll, name, masked: outcome.masked } });
			if (!spent && revision === this.revision) this.clear();
		} else if (outcome.kind === "failed") {
			if (outcome.reason !== "offline") this.spent = null;
			this.fail(outcome.reason, outcome.roll, name);
		}
		this.busy = false;
		this.render();
	}

	fail(reason: FailureReason, roll: Roll, name: string) {
		this.dispatch("failed", { detail: { reason, roll, name } });
	}

	loadStates() {
		this.throwTarget.checked = throwEnabled();
		const states = diceStates();
		this.secretTarget.checked = Boolean(states.hide);
		this.formulaToggleTarget.checked = Boolean(states.formula);
		holdDiceStates(states);
	}

	compose() {
		this.custom = false;
		this.revision += 1;
		this.formulaTarget.value = this.pool.notation();
		this.render();
	}

	render() {
		if (this.pending !== this.spent) this.spent = null;
		const count = (element: HTMLElement) => this.pool.count(Number(element.dataset.die));
		for (const counter of this.counterTargets) {
			counter.textContent = count(counter) ? String(count(counter)) : "";
			counter.hidden = !count(counter);
		}
		for (const chip of this.chipTargets) chip.hidden = !count(chip);
		for (const label of this.chipLabelTargets) label.textContent = `${count(label) > 1 ? count(label) : ""}d${label.dataset.die}`;

		const hasDice = this.chipTargets.some((chip) => !chip.hidden);
		const showFormula = this.formulaToggleTarget.checked;
		const customText = this.custom ? this.formulaTarget.value.trim() : "";

		this.formulaFieldTarget.hidden = !showFormula;
		this.pillsTarget.hidden = showFormula || !hasDice;
		this.customTextTarget.hidden = showFormula || hasDice || !customText;
		this.customTextTarget.textContent = customText;
		const holding = Boolean(this.hasPendingHintTarget) && !showFormula && !hasDice && !customText && this.pending !== 0;
		this.hintTarget.hidden = showFormula || hasDice || Boolean(customText) || holding;
		this.poolTarget.hidden = showFormula || (!hasDice && !customText);

		const modifier = this.custom ? trailingNumber(this.formulaTarget.value) : this.pool.modifier;
		this.modifierTarget.textContent = modifier > 0 ? `+${modifier}` : String(modifier);
		this.rollButtonTarget.disabled = this.busy || !parse(this.formulaTarget.value).ok;
		this.soundTarget.dataset.level = SOUND_LEVELS[diceVolume()];
		this.renderTab(modifier);

		if (!this.hasSubjectValue && !this.joined) return;
		const bonus = this.pending;
		const text = bonus > 0 ? `+${bonus}` : String(bonus);
		if (this.hasPendingHintTarget) {
			this.pendingHintTarget.hidden = !holding;
			this.pendingHintTarget.querySelector("[data-pending-value]")?.replaceChildren(text);
		}
		this.publish(this.shared);
	}

	// The tab mirrors the box's settings while they differ from the default: its modifier (which a chip takes only while
	// shared), a secret roll, which also darkens the tab, and Dados animados off. Its label reads them out.
	renderTab(bonus: number) {
		const opener = this.openerTarget;
		this.tabModifierTarget.textContent = bonus > 0 ? `+${bonus}` : String(bonus);
		this.tabModifierTarget.hidden = bonus === 0;
		// With no eye on the box, a stored secret roll is not the box's to show.
		const secret = this.secretTarget.checked && !this.secretOffValue;
		this.tabSecretTarget.hidden = !secret;
		this.tabThrowTarget.hidden = this.throwTarget.checked;
		const shown = [this.tabModifierTarget, this.tabSecretTarget, this.tabThrowTarget].filter((sign) => !sign.hidden);
		mark(opener, "changed", shown.length > 0);
		mark(opener, "secret", secret);
		const said = shown.map((sign) => (sign === this.tabModifierTarget ? `${sign.title} ${sign.textContent}` : sign.title));
		const name = opener.dataset.name ?? opener.title;
		opener.setAttribute("aria-label", said.length ? `${name}: ${said.join(", ")}` : name);
	}
}

function mark(element: HTMLElement, name: string, on: boolean) {
	if (on) element.dataset[name] = "";
	else delete element.dataset[name];
}
